import * as XLSX from 'xlsx';
import { DEFAULT_COLUMN_MAPPING, HEADER_ALIASES, REQUIRED_MAPPING_KEYS, ROLE_DEFINITIONS, SHEET_NAME } from './constants';
import {
  hashString,
  normalizeKey,
  normalizeQaResult,
  normalizeServiceType,
  normalizeState,
  normalizeTeamMember,
  toDateOnly,
  toIsoDateTime,
  toNullableNumber,
  toNullableText,
  trimText,
} from './normalization';

export function columnLetterToIndex(columnLetter) {
  const letters = String(columnLetter || '').trim().toUpperCase();
  if (!/^[A-Z]+$/.test(letters)) return -1;
  let index = 0;
  for (let i = 0; i < letters.length; i += 1) {
    index = index * 26 + (letters.charCodeAt(i) - 64);
  }
  return index - 1;
}

export function indexToColumnLetter(index) {
  let dividend = index + 1;
  let columnName = '';
  while (dividend > 0) {
    const modulo = (dividend - 1) % 26;
    columnName = String.fromCharCode(65 + modulo) + columnName;
    dividend = Math.floor((dividend - modulo) / 26);
  }
  return columnName;
}

function normalizeHeaderName(value) {
  return trimText(value).replace(/[\s_:\-–—/]+/g, '').toLowerCase();
}

function findHeaderColumn(headers, aliases = []) {
  const normalizedHeaders = headers.map((header) => normalizeHeaderName(header));
  for (const alias of aliases) {
    const target = normalizeHeaderName(alias);
    const exactIndex = normalizedHeaders.findIndex((header) => header === target);
    if (exactIndex >= 0) return exactIndex;

    const suffixIndex = normalizedHeaders.findIndex((header) => header.endsWith(target));
    if (suffixIndex >= 0) return suffixIndex;
  }
  return -1;
}

function resolveMappingWithHeaders(mapping, headers) {
  const resolved = { ...DEFAULT_COLUMN_MAPPING, ...mapping };

  Object.entries(HEADER_ALIASES).forEach(([key, aliases]) => {
    const headerIndex = findHeaderColumn(headers, aliases);
    if (headerIndex >= 0) {
      resolved[key] = indexToColumnLetter(headerIndex);
    }
  });

  // The Design Reviewer role must come from Milestone Design Reviewer when the
  // new Salesforce report field exists. If it does not exist, the app falls
  // back to the legacy Design Reviewer column.
  const milestoneDesignReviewerIndex = findHeaderColumn(headers, ['Milestone Design Reviewer']);
  if (milestoneDesignReviewerIndex >= 0) {
    resolved.design_reviewer = indexToColumnLetter(milestoneDesignReviewerIndex);
    resolved.milestone_design_reviewer = indexToColumnLetter(milestoneDesignReviewerIndex);
  }

  // PE and EE role assignments must use the milestone-specific source fields
  // from the KPI Matrix. Do not use the future-use Milestone PE Engineer /
  // Milestone EE Engineer columns for PE/EE production role metrics.
  const milestoneStructuralEngineerIndex = findHeaderColumn(headers, ['Milestone Structural Engineer']);
  if (milestoneStructuralEngineerIndex >= 0) {
    resolved.pe_engineer = indexToColumnLetter(milestoneStructuralEngineerIndex);
  }

  const milestoneElectricalEngineerIndex = findHeaderColumn(headers, ['Milestone Electrical Engineer']);
  if (milestoneElectricalEngineerIndex >= 0) {
    resolved.ee_engineer = indexToColumnLetter(milestoneElectricalEngineerIndex);
  }

  return resolved;
}

function getCell(row, mapping, key) {
  const letter = mapping[key] || DEFAULT_COLUMN_MAPPING[key];
  const index = columnLetterToIndex(letter);
  if (index < 0) return null;
  return row[index] ?? null;
}

function detectHeaderRow(rows) {
  const maxRows = Math.min(rows.length, 40);
  for (let i = 0; i < maxRows; i += 1) {
    const joined = rows[i].map((cell) => trimText(cell)).join(' | ');
    if (joined.includes('Project: Project Name') && joined.includes('Milestone Type')) {
      return i;
    }
  }
  return 10;
}

function validateMappings(mapping, rows) {
  const errors = [];
  const maxColumns = rows.reduce((max, row) => Math.max(max, row.length), 0);
  REQUIRED_MAPPING_KEYS.forEach((key) => {
    const letter = mapping[key] || DEFAULT_COLUMN_MAPPING[key];
    const index = columnLetterToIndex(letter);
    if (index < 0) errors.push(`${key} has invalid column letter: ${letter}`);
    if (index >= maxColumns) errors.push(`${key} column ${letter} is outside the uploaded sheet range.`);
  });
  return errors;
}

function buildRawRow(headers, row) {
  const raw = {};
  const max = Math.max(headers.length, row.length);
  for (let index = 0; index < max; index += 1) {
    const header = trimText(headers[index]) || indexToColumnLetter(index);
    raw[header] = row[index] ?? null;
  }
  return raw;
}

function cleanRow(row, rawRowData, sourceRowNumber, mapping) {
  const dashboardDateValue = getCell(row, mapping, 'dashboard_date') ?? getCell(row, mapping, 'milestone_end_date');
  const projectName = toNullableText(getCell(row, mapping, 'project_name'));
  const milestoneType = toNullableText(getCell(row, mapping, 'milestone_type'));
  const milestoneDate = toDateOnly(dashboardDateValue);

  const roleValues = {};
  ROLE_DEFINITIONS.forEach((role) => {
    const raw = toNullableText(getCell(row, mapping, role.key));
    roleValues[role.rawField] = raw;
    roleValues[role.normField] = raw ? normalizeTeamMember(raw) : null;
  });

  const customerRestampReason = toNullableText(getCell(row, mapping, 'customer_restamp_reason'));
  const creRestampReasonRaw = toNullableText(getCell(row, mapping, 'cre_restamp_reason'));
  const creRestampReason = milestoneType === 'Restamp Required'
    ? (!creRestampReasonRaw
      ? '(Blank)'
      : creRestampReasonRaw === 'Same as Client'
        ? (customerRestampReason || '(Blank)')
        : creRestampReasonRaw)
    : null;

  const clean = {
    source_row_number: sourceRowNumber,
    row_hash: hashString(JSON.stringify(rawRowData)),
    project_name: projectName,
    project_key: normalizeKey(projectName),
    milestone_type: milestoneType,
    milestone_name: toNullableText(getCell(row, mapping, 'milestone_name')),
    client: toNullableText(getCell(row, mapping, 'client')),
    client_key: normalizeKey(getCell(row, mapping, 'client')),
    state: normalizeState(getCell(row, mapping, 'state')) || null,
    service_type: toNullableText(getCell(row, mapping, 'service_type')),
    service_type_norm: normalizeServiceType(getCell(row, mapping, 'service_type')) || null,
    project_address: toNullableText(getCell(row, mapping, 'project_address')),
    structure_type: toNullableText(getCell(row, mapping, 'structure_type')),
    in_house_vs_external: toNullableText(getCell(row, mapping, 'in_house_vs_external')),
    milestone_qa_result: toNullableText(getCell(row, mapping, 'milestone_qa_result')),
    design_review_in_progress_tat_hours: toNullableNumber(getCell(row, mapping, 'design_review_in_progress_tat_hours')),
    design_in_progress_tat_hours: toNullableNumber(getCell(row, mapping, 'design_in_progress_tat_hours')),
    pe_in_progress_tat_hours: toNullableNumber(getCell(row, mapping, 'pe_in_progress_tat_hours')),
    ee_in_progress_tat_hours: toNullableNumber(getCell(row, mapping, 'ee_in_progress_tat_hours')),
    milestone_qa_result_norm: normalizeQaResult(getCell(row, mapping, 'milestone_qa_result')) || null,
    milestone_design_reviewer_raw: toNullableText(getCell(row, mapping, 'milestone_design_reviewer')),
    milestone_design_reviewer_norm: normalizeTeamMember(getCell(row, mapping, 'milestone_design_reviewer')) || null,
    milestone_ee_engineer_raw: toNullableText(getCell(row, mapping, 'milestone_ee_engineer')),
    milestone_ee_engineer_norm: normalizeTeamMember(getCell(row, mapping, 'milestone_ee_engineer')) || null,
    milestone_pe_engineer_raw: toNullableText(getCell(row, mapping, 'milestone_pe_engineer')),
    milestone_pe_engineer_norm: normalizeTeamMember(getCell(row, mapping, 'milestone_pe_engineer')) || null,

    milestone_date: milestoneDate,
    created_date: toIsoDateTime(getCell(row, mapping, 'created_date')),
    project_start_date: toIsoDateTime(getCell(row, mapping, 'project_start_date')),
    project_end_date: toIsoDateTime(getCell(row, mapping, 'project_end_date')),
    milestone_start_date: toIsoDateTime(getCell(row, mapping, 'milestone_start_date')),
    milestone_end_date: toIsoDateTime(getCell(row, mapping, 'milestone_end_date')),

    ready_for_design_tat_hours: toNullableNumber(getCell(row, mapping, 'ready_for_design_tat_hours')),
    design_tat_hours: toNullableNumber(getCell(row, mapping, 'design_tat_hours')),
    pe_tat_hours: toNullableNumber(getCell(row, mapping, 'pe_tat_hours')),
    ready_for_pe_tat_hours: toNullableNumber(getCell(row, mapping, 'ready_for_pe_tat_hours')),
    total_tat_hours: toNullableNumber(getCell(row, mapping, 'total_tat_hours')),
    ready_for_ee_tat_hours: toNullableNumber(getCell(row, mapping, 'ready_for_ee_tat_hours')),
    ee_tat_hours: toNullableNumber(getCell(row, mapping, 'ee_tat_hours')),
    ready_wet_stamps_tat_hours: toNullableNumber(getCell(row, mapping, 'ready_wet_stamps_tat_hours')),
    customer_restamp_reason: customerRestampReason,
    cre_restamp_reason: creRestampReason,
    on_hold_reason: toNullableText(getCell(row, mapping, 'on_hold_reason')),
    on_hold_other_reason: toNullableText(getCell(row, mapping, 'on_hold_other_reason')),
    resolved_on_hold_reason: toNullableText(getCell(row, mapping, 'on_hold_other_reason')) || toNullableText(getCell(row, mapping, 'on_hold_reason')),
    on_hold_time_hours: toNullableNumber(getCell(row, mapping, 'on_hold_time_hours')),

    raw_row_data: rawRowData,
    ...roleValues,
  };

  return clean;
}

function isBlankDataRow(row, mapping) {
  return !trimText(getCell(row, mapping, 'project_name')) && !trimText(getCell(row, mapping, 'milestone_type'));
}

export async function parseProjectMilestoneWorkbook(file, mappingInput = DEFAULT_COLUMN_MAPPING) {
  const mapping = { ...DEFAULT_COLUMN_MAPPING, ...mappingInput };
  const data = await file.arrayBuffer();
  const workbook = XLSX.read(data, { type: 'array', cellDates: true });
  const sheet = workbook.Sheets[SHEET_NAME] || workbook.Sheets[workbook.SheetNames[0]];

  if (!sheet) {
    throw new Error(`No worksheet found. Expected a sheet named "${SHEET_NAME}".`);
  }

  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: true,
    defval: null,
    blankrows: false,
  });

  const headerRowIndex = detectHeaderRow(rows);
  const headers = rows[headerRowIndex] || [];
  const resolvedMapping = resolveMappingWithHeaders(mapping, headers);

  const validationErrors = validateMappings(resolvedMapping, rows);
  if (validationErrors.length > 0) {
    throw new Error(`Upload validation failed: ${validationErrors.join(' ')}`);
  }

  const dataRows = rows.slice(headerRowIndex + 1);

  const rawRows = [];
  const cleanRows = [];
  let skippedRows = 0;

  dataRows.forEach((row, index) => {
    const sourceRowNumber = headerRowIndex + 2 + index;
    if (isBlankDataRow(row, resolvedMapping)) {
      skippedRows += 1;
      return;
    }

    const rawRowData = buildRawRow(headers, row);
    const clean = cleanRow(row, rawRowData, sourceRowNumber, resolvedMapping);

    if (!clean.project_name || !clean.milestone_type || !clean.milestone_date) {
      skippedRows += 1;
      return;
    }

    rawRows.push({ source_row_number: sourceRowNumber, row_data: rawRowData });
    cleanRows.push(clean);
  });

  const dates = cleanRows.map((row) => row.milestone_date).filter(Boolean).sort();
  const clients = new Set(cleanRows.map((row) => row.client_key).filter(Boolean));
  const states = new Set(cleanRows.map((row) => row.state).filter(Boolean));
  const uniqueTeamMembersByRole = {};

  ROLE_DEFINITIONS.forEach((role) => {
    uniqueTeamMembersByRole[role.key] = new Set(
      cleanRows.map((row) => row[role.normField]).filter(Boolean),
    ).size;
  });

  return {
    sheetName: sheet['!name'] || SHEET_NAME,
    headerRowNumber: headerRowIndex + 1,
    totalRowsFound: dataRows.length,
    validRowsImported: cleanRows.length,
    skippedRows,
    minDate: dates[0] || null,
    maxDate: dates[dates.length - 1] || null,
    uniqueClients: clients.size,
    uniqueStates: states.size,
    uniqueTeamMembersByRole,
    rawRows,
    cleanRows,
    mapping: resolvedMapping,
  };
}
